import React, { useEffect, useState } from 'react';
import { MdErrorOutline, MdTableChart } from 'react-icons/md';
import * as XLSX from 'xlsx';

interface ExcelPreviewProps {
  url: string;
  title?: string;
  height?: number | string;
}

interface SheetData {
  name: string;
  headers: string[];
  rows: (string | number | boolean | null)[][];
}

const colLabel = (i: number): string => {
  let s = '';
  let n = i;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
};

const ExcelPreview: React.FC<ExcelPreviewProps> = ({ url, title, height = 420 }) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setSheets([]);
    setActiveSheet(0);

    const controller = new AbortController();

    const proxiedUrl = url.match(/^https?:\/\//)
      ? url.replace(/^https?:\/\/[^/]+/, '')
      : url;

    fetch(proxiedUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: 'array' });

        const parsed: SheetData[] = workbook.SheetNames.map((name) => {
          const ws = workbook.Sheets[name];
          const raw = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(ws, {
            header: 1,
            defval: null,
          });

          const headers = ((raw[0] as (string | number | boolean | null)[]) ?? []).map((h) =>
            h !== null ? String(h) : ''
          );
          return { name, headers, rows: raw.slice(1) as (string | number | boolean | null)[][] };
        });

        setSheets(parsed);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError('Could not load the file. Please download it to view.');
        setLoading(false);
      });

    return () => controller.abort();
  }, [url]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center bg-slate-50 gap-3" style={{ height }}>
        <div className="w-8 h-8 border-[3px] border-green-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs font-medium text-slate-400">Loading spreadsheet…</p>
      </div>
    );
  }

  if (error || sheets.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center bg-slate-50 text-center px-8"
        style={{ height }}
      >
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-3">
          <MdErrorOutline size={26} className="text-red-400" />
        </div>
        <p className="text-slate-600 font-semibold text-sm">Preview unavailable</p>
        <p className="text-slate-400 text-xs mt-1 max-w-[220px]">
          {error ?? 'This sheet appears to be empty.'}
        </p>
      </div>
    );
  }

  const current = sheets[activeSheet];
  const colCount = current.headers.length;

  return (
    <div className="flex flex-col bg-white overflow-hidden" style={{ height }}>
      <div className="flex items-center justify-between px-4 py-2 bg-green-700 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MdTableChart size={16} className="text-green-200 shrink-0" />
          {title && (
            <span className="text-xs font-semibold text-white truncate">{title}</span>
          )}
        </div>
        <span className="text-[10px] text-green-300 shrink-0 ml-2">
          {current.rows.length} rows · {colCount} cols
        </span>
      </div>

      {sheets.length > 1 && (
        <div className="flex items-end gap-0.5 px-3 pt-1.5 bg-green-800 shrink-0 overflow-x-auto">
          {sheets.map((sheet, idx) => (
            <button
              key={sheet.name}
              onClick={() => setActiveSheet(idx)}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-t-md whitespace-nowrap transition-colors ${activeSheet === idx
                ? 'bg-white text-green-800'
                : 'bg-green-700/60 text-green-200 hover:bg-green-700 hover:text-white'
                }`}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {colCount === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-xs">
            This sheet is empty
          </div>
        ) : (
          <table className="border-separate border-spacing-0 text-xs" style={{ minWidth: '100%' }}>
            <thead
              className="sticky top-0 z-20"
              style={{ boxShadow: '0 2px 0 #e2e8f0' }}
            >
              <tr>
                <th className="sticky left-0 z-30 w-10 min-w-[2.5rem] bg-slate-100 border-b border-r border-slate-200 select-none" />
                {current.headers.map((_, i) => (
                  <th
                    key={i}
                    className="bg-slate-100 border-b border-r border-slate-200 px-2 py-1 text-center text-[10px] font-bold text-slate-500 select-none whitespace-nowrap"
                  >
                    {colLabel(i)}
                  </th>
                ))}
              </tr>

              <tr>
                <td className="sticky left-0 z-30 bg-slate-50 border-b-2 border-r border-slate-300 px-2 py-1.5 text-center text-[10px] font-bold text-slate-400 select-none w-10 min-w-[2.5rem]">
                  1
                </td>
                {current.headers.map((h, i) => (
                  <th
                    key={i}
                    className="bg-green-50 border-b-2 border-r border-slate-200 px-3 py-1.5 text-left text-[11px] font-bold text-green-900 whitespace-nowrap"
                    title={h}
                  >
                    {h || `Col ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {current.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount + 1}
                    className="text-center text-slate-400 py-10 text-xs border-b border-slate-100"
                  >
                    No data rows
                  </td>
                </tr>
              ) : (
                current.rows.map((row, rIdx) => {
                  const isEven = rIdx % 2 === 0;
                  return (
                    <tr
                      key={rIdx}
                      className={`group transition-colors hover:bg-green-50/70 ${isEven ? 'bg-white' : 'bg-slate-50/60'
                        }`}
                    >
                      <td
                        className={`sticky left-0 z-10 border-b border-r border-slate-100 px-2 py-1.5 text-center text-[10px] text-slate-300 font-medium select-none w-10 min-w-[2.5rem] ${isEven ? 'bg-slate-50' : 'bg-slate-100/70'
                          } group-hover:bg-green-50/80`}
                      >
                        {rIdx + 2}
                      </td>
                      {current.headers.map((_, cIdx) => {
                        const val = row[cIdx];
                        const str = val !== null && val !== undefined ? String(val) : '';
                        return (
                          <td
                            key={cIdx}
                            className="border-b border-r border-slate-100 px-3 py-1.5 text-slate-700 whitespace-nowrap max-w-[220px] overflow-hidden text-ellipsis"
                            title={str}
                          >
                            {str}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-4 px-4 py-1.5 bg-green-50 border-t border-green-100">
        <span className="text-[10px] text-green-700 font-medium">
          Sheet: {current.name}
        </span>
        <span className="text-[10px] text-slate-400">
          {current.rows.length} row{current.rows.length !== 1 ? 's' : ''}
        </span>
        <span className="text-[10px] text-slate-400">
          {colCount} column{colCount !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default ExcelPreview;
